import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, ExternalLink, Share2, Check, Copy,
  ShoppingCart, Music2, Apple, Youtube, Headphones,
  Calendar, Tag, Disc, Heart, Star, Zap, Disc3, Pencil
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CoverImage } from "../components/ReleaseCard";
import ReleaseCard from "../components/ReleaseCard";
import ReleaseModal from "../components/ReleaseModal";
import ArtistLinks from "../components/ArtistLinks";
import PartnerSpot from "../components/PartnerSpot";
import CommentsSection from "../components/CommentsSection";
import HypeButton from "../components/HypeButton";
import { formatDate, getSpotifyEmbedUrl, applyAffiliateLink, nameToSlug } from "../utils/affiliateHelper";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";
import { useAuth } from "../context/AuthContext";

/* Parse a tracklist entry string and link artist / remixer names */
function LinkedTrack({ entry }) {
  if (typeof entry !== "string") return <span className="text-zinc-200 text-sm flex-1">{String(entry)}</span>;

  const parts = [];
  let remaining = entry;

  // Pattern 1: leading "Artist - " credit
  const dashMatch = remaining.match(/^(.+?)\s*[-\u2013]\s*/);
  if (dashMatch) {
    const artistName = dashMatch[1];
    parts.push(
      <Link key="lead" to={`/artist/${nameToSlug(artistName)}`}
        className="text-rose-400 hover:text-rose-300 transition-colors"
        onClick={(e) => e.stopPropagation()}>
        {artistName}
      </Link>
    );
    parts.push(<span key="sep"> – </span>);
    remaining = remaining.slice(dashMatch[0].length);
  }

  // Pattern 2: "(Remixer Remix/Mix)" anywhere in the remaining text
  const remixMatch = remaining.match(/^(.*?)\((.+?)\s+(Remix|Mix)\b/i);
  if (remixMatch) {
    const before = remixMatch[1];
    const remixer = remixMatch[2];
    const versionWord = remixMatch[3];
    const after = remaining.slice(remixMatch[0].length); // everything after "Remix|Mix"
    if (before) parts.push(<span key="before">{before}</span>);
    parts.push(<span key="open">(</span>);
    parts.push(
      <Link key="remixer" to={`/artist/${nameToSlug(remixer)}`}
        className="text-rose-400 hover:text-rose-300 transition-colors"
        onClick={(e) => e.stopPropagation()}>
        {remixer}
      </Link>
    );
    parts.push(<span key="vtype"> {versionWord}{after}</span>);
  } else {
    parts.push(<span key="text">{remaining}</span>);
  }

  return <span className="text-zinc-200 text-sm flex-1">{parts}</span>;
}

function StreamBtn({ href, color, icon, label }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-105 hover:brightness-110 shadow-lg"
      style={{ background: color }}>
      {icon}
      {label}
      <ExternalLink size={12} className="opacity-50 ml-auto" />
    </a>
  );
}

export default function ReleasePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [affiliateRules, setAffiliateRules] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [selectedSimilar, setSelectedSimilar] = useState(null);
  const { publicUser, isPublicAuth, login, publicApi } = usePublicAuth();
  const { user: adminUser } = useAuth();
  const isAdmin = adminUser?.role === "admin" || adminUser?.role === "developer";
  const [favorited, setFavorited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [hypeStatus, setHypeStatus] = useState(null);

  useEffect(() => {
    if (!isPublicAuth || !release?.id || !publicUser?.is_supporter) return;
    publicApi("get", "/me/favorites")
      .then(res => setFavorited(res.data.some(f => f.item_id === release.id)))
      .catch(() => {});
  }, [isPublicAuth, release?.id, publicUser?.is_supporter, publicApi]);

  const handleFavorite = async () => {
    if (!isPublicAuth) { login(); return; }
    if (!publicUser?.is_supporter) return;
    setFavLoading(true);
    try {
      if (favorited) {
        await publicApi("delete", `/me/favorites/${release.id}`);
        setFavorited(false);
      } else {
        await publicApi("post", "/me/favorites", { item_id: release.id, item_type: "release" });
        setFavorited(true);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    } finally {
      setFavLoading(false);
    }
  };

  useEffect(() => {
    api.get("/affiliate/public").then(r => setAffiliateRules(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/releases/slug/${slug}`)
      .then(res => { setRelease(res.data); setLoading(false); })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  const SITE = "https://handsupwillneverdie.de";

  useEffect(() => {
    if (release) {
      document.title = `${release.interpret} – ${release.titel} | HUWND`;
      api.get(`/releases/slug/${release.post_name}/similar`)
        .then(res => setSimilar(res.data || []))
        .catch(() => setSimilar([]));
    }
    return () => { document.title = "HUWND"; };
  }, [release]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch { /* ignore */ }
  };

  const af = (url) => applyAffiliateLink(url, affiliateRules);
  const spotifyEmbed = release ? getSpotifyEmbedUrl(release.spotify_preview_track || release.spotify) : null;

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-20 animate-pulse space-y-6">
        <div className="h-8 bg-zinc-800 rounded w-48" />
        <div className="flex gap-8">
          <div className="w-64 h-64 bg-zinc-800 rounded-2xl flex-shrink-0" />
          <div className="flex-1 space-y-4">
            <div className="h-10 bg-zinc-800 rounded w-3/4" />
            <div className="h-6 bg-zinc-800 rounded w-1/2" />
            <div className="h-4 bg-zinc-800 rounded w-1/3" />
          </div>
        </div>
      </div>
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      <Header />
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <Disc size={48} className="text-zinc-700 mb-4" />
        <h1 className="text-2xl font-black mb-2">Release nicht gefunden</h1>
        <p className="text-zinc-500 mb-6">Dieses Release existiert nicht oder wurde entfernt.</p>
        <Link to="/search" className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg transition-colors">
          Zurück zur Musik
        </Link>
      </div>
    </div>
  );

  if (!release) return null;

  const hasLinks = release.beatport || release.spotify || release.amazon || release.apple_music || release.deezer || release.youtube;
  const hasTracks = release.tracklist?.length > 0;
  const pageTitle = `${release.interpret} – ${release.titel} | HUWND`;
  const pageDesc = `${release.typ || "Release"} von ${release.interpret}${release.erscheinungsdatum ? ` (${release.erscheinungsdatum.slice(0,4)})` : ""}. Jetzt auf Hands Up Will Never Die entdecken.`;
  const canonicalUrl = `${SITE}/release/${release.post_name}`;
  const releaseJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MusicAlbum",
    "name": release.titel,
    "byArtist": { "@type": "MusicGroup", "name": release.interpret },
    "datePublished": release.erscheinungsdatum?.slice(0,10),
    "image": release.cover_url || undefined,
    "url": canonicalUrl,
    ...(release.spotify ? { "sameAs": release.spotify } : {}),
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="music.album" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        {release.cover_url ? <meta property="og:image" content={release.cover_url} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {release.cover_url ? <meta name="twitter:image" content={release.cover_url} /> : null}
        <script type="application/ld+json">{releaseJsonLd}</script>
      </Helmet>
      <Header />

      {/* Blurred hero background */}
      <div className="relative overflow-hidden">
        {release.cover_url && (
          <div className="absolute inset-0 opacity-15">
            <img src={release.cover_url} alt="" className="w-full h-full object-cover scale-110 blur-3xl" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/50 via-zinc-950/80 to-zinc-950" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-12">
          {/* Back breadcrumb + Admin Edit */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-medium transition-colors group">
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              Zurück
            </button>

            {isAdmin && release?.id && (
              <Link
                to={`/admin/releases/${release.id}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-edit-release-btn"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:text-amber-300 text-xs font-semibold transition-all"
              >
                <Pencil size={12} />
                Release bearbeiten
              </Link>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-8">
            {/* Cover */}
            <div className="flex-shrink-0">
              <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <CoverImage
                  cover_url={release.cover_url}
                  interpret={release.interpret}
                  titel={release.titel}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {/* Meta badges */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {release.typ && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-rose-900/40 text-rose-300 border border-rose-700/30">
                    {release.typ}
                  </span>
                )}
                {release.label && (
                  <Link
                    to={release.label_slug ? `/label/${release.label_slug}` : `/labels?search=${encodeURIComponent(release.label)}`}
                    data-testid="release-label-link"
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors">
                    <Disc3 size={10} />
                    {release.label}
                  </Link>
                )}
                {release.erscheinungsdatum && (
                  <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                    <Calendar size={12} />
                    {formatDate(release.erscheinungsdatum)}
                  </span>
                )}
              </div>

              <ArtistLinks
                release={release}
                className="block text-3xl sm:text-4xl lg:text-5xl font-black font-heading leading-tight mb-2"
              />
              <p className="text-zinc-300 text-xl sm:text-2xl font-medium mb-6">
                {release.titel}
              </p>

              {/* Tags / Categories */}
              {release.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {release.categories.filter(c => c !== release.typ).slice(0, 6).map(cat => (
                    <span key={cat} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                      style={{ background: "rgba(225,29,72,0.1)", color: "#fb7185", border: "1px solid rgba(225,29,72,0.2)" }}>
                      <Tag size={9} />
                      {cat}
                    </span>
                  ))}
                </div>
              )}

              {/* Share + Favorite + Hype */}
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <button
                  onClick={handleShare}
                  data-testid="release-page-share-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                  style={{ background: copied ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.06)", color: copied ? "#86efac" : "#a1a1aa", border: `1px solid ${copied ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.08)"}` }}
                >
                  {copied ? <Check size={14} /> : <Share2 size={14} />}
                  {copied ? "Link kopiert!" : "Seite teilen"}
                </button>

                {publicUser?.is_supporter ? (
                  <button
                    onClick={handleFavorite}
                    disabled={favLoading}
                    data-testid="favorite-release-btn"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      favorited
                        ? "text-rose-400"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                    style={{ background: favorited ? "rgba(220,38,38,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${favorited ? "rgba(220,38,38,0.3)" : "rgba(255,255,255,0.06)"}` }}
                  >
                    <Heart size={14} className={favorited ? "fill-rose-400" : ""} />
                    {favorited ? "Favorit" : "Favorit"}
                  </button>
                ) : (
                  <Link
                    to="/supporter"
                    data-testid="favorite-supporter-hint"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
                    title="Supporter werden zum Favorisieren"
                  >
                    <Heart size={12} /> <Star size={10} className="text-rose-500/60" />
                  </Link>
                )}

                {/* Hype Button */}
                {release?.id && (
                  <div
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)" }}
                  >
                    <HypeButton releaseId={release.id} size={15} showCount showLabel onStatusLoad={setHypeStatus} />
                  </div>
                )}
              </div>

              {/* HYPE Banner – prominent, wenn release gehyped wurde */}
              {hypeStatus?.hype_count >= 1 && (
                <div
                  data-testid="hype-banner"
                  className="flex items-center gap-3 mb-4 px-4 py-3 rounded-xl relative overflow-hidden"
                  style={{
                    background: "linear-gradient(135deg, rgba(245,158,11,0.12), rgba(217,119,6,0.08))",
                    border: "1px solid rgba(245,158,11,0.3)",
                    boxShadow: "0 0 24px rgba(245,158,11,0.12)",
                  }}
                >
                  <Zap size={20} className="text-amber-400 flex-shrink-0" fill="currentColor" />
                  <div className="flex-1 min-w-0">
                    <p className="text-amber-300 text-sm font-black uppercase tracking-wider leading-none">Community Hype</p>
                    <p className="text-amber-500/70 text-xs mt-0.5">
                      {hypeStatus.hype_count} Supporter {hypeStatus.hype_count === 1 ? "hat" : "haben"} dieses Release gehyped
                    </p>
                  </div>
                  <span className="text-4xl font-black text-amber-400/15 select-none flex-shrink-0">⚡</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 space-y-10">

        {/* Streaming Links */}
        {hasLinks && (
          <div data-testid="release-page-links">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Hören &amp; Kaufen</h2>
            <div className="flex flex-wrap gap-2.5">
              <StreamBtn href={af(release.beatport)} color="linear-gradient(135deg,#FF6F00,#E05E00)" icon={<ShoppingCart size={15} />} label="Beatport" />
              <StreamBtn href={af(release.spotify)} color="linear-gradient(135deg,#1DB954,#158a3e)" icon={<Music2 size={15} />} label="Spotify" />
              <StreamBtn href={af(release.amazon)} color="linear-gradient(135deg,#FF9900,#cc7a00)" icon={<ShoppingCart size={15} />} label="Amazon Music" />
              <StreamBtn href={af(release.apple_music)} color="linear-gradient(135deg,#fc3c44,#c4162a)" icon={<Apple size={15} />} label="Apple Music" />
              <StreamBtn href={af(release.deezer)} color="linear-gradient(135deg,#a238ff,#7000e0)" icon={<Headphones size={15} />} label="Deezer" />
              <StreamBtn href={release.youtube} color="linear-gradient(135deg,#FF0000,#cc0000)" icon={<Youtube size={15} />} label="YouTube" />
            </div>
          </div>
        )}

        {/* Label CTA */}
        {release.label && (
          <div data-testid="label-cta-section"
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl bg-zinc-900/60 border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Disc3 size={16} className="text-zinc-400" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">{release.label}</p>
                <p className="text-xs text-zinc-500">Gehört dieses Label dir?</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to={release.label_slug ? `/label/${release.label_slug}` : `/labels?search=${encodeURIComponent(release.label)}`}
                data-testid="label-cta-view"
                className="px-3 py-1.5 text-xs font-semibold rounded-xl border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
                Label-Profil
              </Link>
              <Link to="/label-studio" data-testid="label-cta-claim"
                className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white border border-white/10 transition-colors">
                Beanspruchen &rarr;
              </Link>
            </div>
          </div>
        )}

        {/* Spotify Embed */}
        {spotifyEmbed && (
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Vorschau</h2>
            <iframe
              src={spotifyEmbed}
              width="100%"
              height="152"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="rounded-xl"
              title="Spotify Player"
            />
          </div>
        )}

        {/* Tracklist */}
        {hasTracks && (
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
              Tracklist
              <span className="ml-2 normal-case tracking-normal font-normal text-zinc-700">
                {release.tracklist.length} {release.tracklist.length === 1 ? "Track" : "Tracks"}
              </span>
            </h2>
            <div className="bg-zinc-900 rounded-2xl overflow-hidden border border-white/5">
              {release.tracklist.map((track, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                  <span className="text-zinc-700 text-xs font-mono w-6 text-right flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-zinc-200 text-sm flex-1"><LinkedTrack entry={track} /></span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Partner Spot */}
        <PartnerSpot position="release" />

        {/* Kommentare */}
        {release?.id && <CommentsSection releaseId={release.id} />}

        {/* Similar Releases */}
        {similar.length > 0 && (
          <div data-testid="similar-releases">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
              Ähnliche Releases
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {similar.map(r => (
                <ReleaseCard key={r.id} release={r} onClick={setSelectedSimilar} />
              ))}
            </div>
          </div>
        )}

      </div>

      <Footer />

      {selectedSimilar && (
        <ReleaseModal release={selectedSimilar} onClose={() => setSelectedSimilar(null)} />
      )}
    </div>
  );
}
